import React, { useState, useEffect } from "react";
import { Avatar, Input, Select, message, Tooltip } from "antd";
import UserInitialsAvatar from "./UserInitialsAvatar";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { patchAuthMethod } from "../../../services/HttpServices";
import { getAllUsers } from "../../../actionCreator/userManagementActionCreator";
import {
  getLeadStatusList,
  getLeadRatingList,
} from "../../../actionCreator/leadListingActionCreater";
import {
  getLeadDetails,
  updateLeadDetails,
} from "../../../actionCreator/leadDetailsActionCreator";
import UpdateLead from "../../lead-search/UpdateLead";
import { PanelRightClose } from "lucide-react";

const LeadDetailSidebar = ({ activeChat, onAssignChat, onStatusChange, onClose }) => {
  const dispatch = useDispatch();

  const usersData = useSelector((state) => state.UserManagementReducer.users);
  const users = Array.isArray(usersData) ? usersData : (usersData?.content || []);

  const stages = useSelector((state) => state.leadListingReducer.leadStatusList) || [];
  const ratings = useSelector((state) => state.leadListingReducer.leadRatingList) || [];
  const leadDetails = useSelector((state) => state.LeadDetailsReducer.leadDetails) || null;

  const [isUpdating, setIsUpdating] = useState(false);
  const [assignedUserId, setAssignedUserId] = useState(null);
  const [isLoadingLead, setIsLoadingLead] = useState(false);

  const getLeadOwnerName = () => {
    if (!leadDetails) return "";
    if (leadDetails.ownerName) return leadDetails.ownerName;
    const empId = leadDetails.employeeId || (typeof leadDetails.employee === "object" ? leadDetails.employee?.id : leadDetails.employee);
    if (empId) {
      const match = users.find((u) => u.id === empId);
      if (match) {
        return `${match.firstName || ""} ${match.lastName || ""}`.trim() || match.login;
      }
    }
    return "";
  };
  const leadOwnerName = getLeadOwnerName();

  const getAssignedAgentName = () => {
    if (assignedUserId) {
      const match = users.find((u) => u.id === assignedUserId);
      if (match) {
        return `${match.firstName || ""} ${match.lastName || ""}`.trim() || match.login;
      }
    }
    if (activeChat?.agent && activeChat.agent !== "Unassigned") {
      return activeChat.agent;
    }
    return "";
  };
  const assignedAgentName = getAssignedAgentName();

  const displayAgentName = assignedAgentName || leadOwnerName || "Unassigned";

  useEffect(() => {
    setAssignedUserId(activeChat?.assignedUserId || null);
  }, [activeChat?.assignedUserId]);

  useEffect(() => {
    dispatch(getAllUsers({ sort: "firstName,asc", size: 1000 }));
    dispatch(getLeadStatusList());
    dispatch(getLeadRatingList());
  }, [dispatch]);

  useEffect(() => {
    if (activeChat?.clientLeadId) {
      setIsLoadingLead(true);
      dispatch(getLeadDetails(activeChat.clientLeadId))
        .then(() => setIsLoadingLead(false))
        .catch(() => setIsLoadingLead(false));
    } else {
      setIsLoadingLead(false);
    }
  }, [activeChat?.clientLeadId, dispatch]);

  const handleAssignAgent = (userId) => {
    if (!activeChat?.conversationId) return;

    setAssignedUserId(userId || null);
    patchAuthMethod(
      `/api/whatsapp-conversations`,
      `${activeChat.conversationId}/assign`,
      {
        assignedUserId: userId || null,
      },
    )
      .then(() => {
        message.success("Agent assigned successfully");
        if (activeChat) {
          activeChat.assignedUserId = userId || null;
          if (userId) {
            const assignedUser = users.find((u) => u.id === userId);
            activeChat.agent = assignedUser
              ? `${assignedUser.firstName || ""} ${assignedUser.lastName || ""}`.trim() ||
                assignedUser.login
              : "Unassigned";
          } else {
            activeChat.agent = "Unassigned";
          }
        }
        if (onAssignChat) {
          onAssignChat(userId || null);
        }
      })
      .catch((err) => {
        console.error("Failed to assign agent", err);
        message.error("Failed to assign agent");
        setAssignedUserId(activeChat?.assignedUserId || null);
      });
  };

  const handleStatusChange = (statusId) => {
    if (!leadDetails) return;

    const selectedStatus = stages.find((s) => s.id === statusId);
    if (!selectedStatus) return;

    setIsUpdating(true);
    const updatedLead = {
      ...leadDetails,
      status: statusId,
    };

    dispatch(updateLeadDetails(updatedLead, true))
      .then((res) => {
        if (res instanceof Error || (res && res.response)) {
          console.error("Failed to update lead status", res);
          message.error("Failed to update lead status");
        } else {
          message.success("Lead status updated successfully");
          if (activeChat) {
            activeChat.stage = selectedStatus.text;
            if (onStatusChange) {
              onStatusChange(activeChat.conversationId, selectedStatus.text);
            }
          }
        }
      })
      .catch((err) => {
        console.error("Failed to update lead status", err);
        message.error("Failed to update lead status");
      })
      .finally(() => {
        setIsUpdating(false);
      });
  };

  const handleRatingChange = (ratingText) => {
    if (!leadDetails) return;

    setIsUpdating(true);
    const updatedLead = {
      ...leadDetails,
      leadRating: ratingText,
    };

    dispatch(updateLeadDetails(updatedLead, true))
      .then((res) => {
        if (res instanceof Error || (res && res.response)) {
          console.error("Failed to update lead rating", res);
          message.error("Failed to update lead rating");
        } else {
          message.success("Lead rating updated successfully");
          if (activeChat) {
            activeChat.rating = ratingText;
          }
        }
      })
      .catch((err) => {
        console.error("Failed to update lead rating", err);
        message.error("Failed to update lead rating");
      })
      .finally(() => {
        setIsUpdating(false);
      });
  };

  const currentStatusId =
    leadDetails?.status ||
    stages.find((s) => s.text === activeChat.stage)?.id ||
    undefined;

  return (
    <div className="tw-w-full lg:tw-w-[308px] lg:tw-min-w-[308px] tw-border-t lg:tw-border-t-0 tw-border-b-0 tw-border-r-0 lg:tw-border-l tw-border-solid tw-border-border-secondary tw-bg-white tw-flex tw-flex-col tw-overflow-y-auto tw-min-h-0">
      <div className="tw-px-5 tw-py-3.5 tw-border-t-0 tw-border-b tw-border-r-0 tw-border-l-0 tw-border-solid tw-border-border-secondary tw-shrink-0 tw-flex tw-items-center tw-justify-between">
        <h3 className="tw-text-base tw-font-semibold tw-text-text-primary tw-m-0">
          Lead Detail
        </h3>
        {onClose && (
          <Tooltip title="Collapse Lead Details">
            <button
              type="button"
              onClick={onClose}
              className="tw-w-7 tw-h-7 tw-rounded-lg tw-border-0 tw-bg-transparent hover:tw-bg-slate-100 tw-text-slate-400 hover:tw-text-slate-700 tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-transition-colors"
              aria-label="Collapse Lead Details"
            >
              <PanelRightClose size={16} />
            </button>
          </Tooltip>
        )}
      </div>

      <div className="tw-px-4 tw-py-4 tw-shrink-0">
        <div className="tw-flex tw-justify-between tw-items-center tw-mb-3">
          <span className="tw-text-xs tw-text-text-primary tw-font-semibold">
            Guest Info
          </span>
          <span className="tw-flex tw-items-center tw-gap-1 tw-text-[#25D366] tw-text-xs tw-font-bold">
            ★ {activeChat.rating}
          </span>
        </div>

        <div className="tw-flex tw-flex-col tw-items-center tw-mb-5 tw-mt-2">
          {activeChat.clientLeadId ? (
            <Link
              to={`/lead-details/${activeChat.clientLeadId}`}
              className="tw-flex tw-flex-col tw-items-center tw-group tw-no-underline hover:tw-opacity-85"
            >
              <UserInitialsAvatar
                name={activeChat.name}
                phone={activeChat.phone}
                src={activeChat.avatar}
                size={72}
                className="tw-mb-3 tw-shadow-sm group-hover:tw-scale-105 tw-transition-all"
              />
              <h4 className="tw-text-lg tw-font-bold tw-text-text-primary tw-m-0 tw-mb-1 group-hover:tw-text-[#075E54] tw-transition-colors">
                {activeChat.name}
              </h4>
              {activeChat.clientLeadName &&
                activeChat.clientLeadName !==
                  "Lead Details Hidden (Permission Denied)" && (
                  <p className="tw-text-sm tw-font-semibold tw-text-[#075E54] tw-m-0 tw-mb-1 tw-flex tw-items-center tw-gap-1 group-hover:tw-underline">
                    Lead: {activeChat.clientLeadName}
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      className="tw-inline-block tw-ml-0.5"
                    >
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                      <polyline points="15 3 21 3 21 9"></polyline>
                      <line x1="10" y1="14" x2="21" y2="3"></line>
                    </svg>
                  </p>
                )}
            </Link>
          ) : (
            <>
              <UserInitialsAvatar
                name={activeChat.name}
                phone={activeChat.phone}
                src={activeChat.avatar}
                size={72}
                className="tw-mb-3 tw-shadow-sm"
              />
              <h4 className="tw-text-lg tw-font-bold tw-text-text-primary tw-m-0 tw-mb-1">
                {activeChat.name}
              </h4>
              {activeChat.clientLeadName &&
                activeChat.clientLeadName !==
                  "Lead Details Hidden (Permission Denied)" && (
                  <p className="tw-text-sm tw-font-semibold tw-text-[#075E54] tw-m-0 tw-mb-1">
                    Lead: {activeChat.clientLeadName}
                  </p>
                )}
            </>
          )}
          <div className="tw-flex tw-items-center tw-gap-2 tw-mt-1">
            <span className="tw-text-xs tw-text-sub-text tw-font-semibold">
              Assign Agent:
            </span>
            <span className="tw-text-xs tw-text-text-primary tw-font-semibold">
              {displayAgentName}
            </span>
          </div>
        </div>

        <div className="tw-bg-[#F8F9FA] tw-rounded-xl tw-p-4 tw-space-y-4">
          <div>
            <label className="tw-text-xs tw-text-sub-text tw-font-semibold tw-block tw-mb-1.5">
              Phone
            </label>
            <Input
              value={leadDetails?.mobile || leadDetails?.phoneNo || activeChat.phone}
              readOnly
              suffix={
                <svg
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#9ca3af"
                  strokeWidth="2"
                  className="tw-cursor-pointer hover:tw-stroke-gray-700"
                  onClick={() =>
                    navigator.clipboard.writeText(activeChat.phone)
                  }
                >
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
              }
              className="tw-rounded-lg tw-bg-white tw-text-text-primary tw-border-gray-200 tw-text-xs tw-font-medium tw-h-9 focus:tw-border-gray-300 hover:tw-border-gray-300"
            />
          </div>

          <div>
            <label className="tw-text-xs tw-text-sub-text tw-font-semibold tw-block tw-mb-1.5">
              Email
            </label>
            <Input
              value={leadDetails?.emailId || (activeChat.email !== "N/A" ? activeChat.email : "")}
              readOnly
              suffix={
                <div className="tw-flex tw-items-center tw-gap-2">
                  {(leadDetails?.emailId || (activeChat.email && activeChat.email !== "N/A")) && (
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#9ca3af"
                      strokeWidth="2"
                      className="tw-cursor-pointer hover:tw-stroke-gray-700"
                      onClick={() =>
                        navigator.clipboard.writeText(leadDetails?.emailId || activeChat.email)
                      }
                    >
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                  )}
                  {activeChat?.clientLeadId && (
                    <UpdateLead
                      title="Email Address"
                      fieldName="emailId"
                      defaultValue={leadDetails?.emailId || (activeChat.email !== "N/A" ? activeChat.email : "")}
                      id={activeChat.clientLeadId}
                      callback={(res) => {
                        dispatch(getLeadDetails(activeChat.clientLeadId));
                        if (res?.emailId && activeChat) {
                          activeChat.email = res.emailId;
                        }
                      }}
                    />
                  )}
                </div>
              }
              className="tw-rounded-lg tw-bg-white tw-text-text-primary tw-border-gray-200 tw-text-xs tw-font-medium tw-h-9 focus:tw-border-gray-300 hover:tw-border-gray-300"
            />
          </div>
        </div>
      </div>

      <div className="tw-px-4 tw-py-4 tw-shrink-0">
        <span className="tw-text-xs tw-text-text-primary tw-font-semibold tw-block tw-mb-3">
          Lead Status
        </span>

        <div className="tw-space-y-4">
          <div>
            <label className="tw-text-xs tw-text-sub-text tw-font-medium tw-block tw-mb-2">
              Lead Status
            </label>
            <Select
              value={currentStatusId}
              className="tw-w-full custom-stage-select"
              placeholder="Select Status"
              loading={isLoadingLead || isUpdating}
              disabled={
                isLoadingLead || isUpdating || !activeChat?.clientLeadId
              }
              onChange={handleStatusChange}
              options={stages.map((s) => ({
                value: s.id,
                label: s.text,
              }))}
              style={{ height: "38px" }}
            />
          </div>

          <div>
            <label className="tw-text-xs tw-text-sub-text tw-font-medium tw-block tw-mb-2">
              Lead Rating
            </label>
            <Select
              value={leadDetails?.leadRating || activeChat.rating || undefined}
              className="tw-w-full"
              placeholder="Select Rating"
              loading={isLoadingLead || isUpdating}
              disabled={
                isLoadingLead || isUpdating || !activeChat?.clientLeadId
              }
              onChange={handleRatingChange}
              options={ratings.map((r) => ({
                value: r.ratingText,
                label: r.ratingText,
              }))}
              style={{ height: "38px" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDetailSidebar;
